import React, { useState, useEffect } from 'react';
import { supabase } from '../supabaseClient';
import { ArrowDownCircle, ArrowUpCircle, Wallet, Plus } from 'lucide-react';

const INCOME_CATEGORIES = [
  { value: 'salary', label: 'Salary / Wages' },
  { value: 'business', label: 'Business' },
  { value: 'investment', label: 'Investment Income' },
  { value: 'other', label: 'Other Income' },
];

const EXPENSE_CATEGORIES = [
  { value: 'housing', label: 'Housing' },
  { value: 'utilities', label: 'Utilities' },
  { value: 'food', label: 'Food & Groceries' },
  { value: 'transportation', label: 'Transportation' },
  { value: 'debt', label: 'Debt Payments' },
  { value: 'insurance', label: 'Insurance' },
  { value: 'entertainment', label: 'Entertainment' },
  { value: 'savings', label: 'Savings & Investing' },
  { value: 'other', label: 'Other' },
];

const emptyForm = (type) => ({
  name: '',
  amount: '',
  category: type === 'income' ? 'salary' : 'housing',
});

const formatCurrency = (amount) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(amount || 0);

const Budget = ({ session, liabilities = [] }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(null); // 'income' | 'expense' | null
  const [editingItem, setEditingItem] = useState(null);
  const [form, setForm] = useState(emptyForm('expense'));
  const [includeLiabilityPayments, setIncludeLiabilityPayments] = useState(() => {
    const saved = localStorage.getItem('budget_includeLiabilityPayments');
    return saved === null ? true : saved === 'true';
  });

  useEffect(() => {
    fetchItems();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    localStorage.setItem('budget_includeLiabilityPayments', String(includeLiabilityPayments));
  }, [includeLiabilityPayments]);

  const fetchItems = async () => {
    try {
      const { data, error } = await supabase
        .from('budget_items')
        .select('*')
        .eq('user_id', session.user.id)
        .order('created_at', { ascending: true });

      if (error) throw error;
      setItems(data || []);
    } catch (error) {
      console.error('Error fetching budget items:', error);
    } finally {
      setLoading(false);
    }
  };

  const openForm = (type) => {
    setEditingItem(null);
    setForm(emptyForm(type));
    setShowForm(type);
  };

  const editItem = (item) => {
    setEditingItem(item);
    setForm({ name: item.name, amount: item.amount.toString(), category: item.category || 'other' });
    setShowForm(item.type);
  };

  const cancelForm = () => {
    setShowForm(null);
    setEditingItem(null);
  };

  const saveItem = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingItem) {
        const { error } = await supabase
          .from('budget_items')
          .update({
            name: form.name,
            amount: parseFloat(form.amount) || 0,
            category: form.category,
          })
          .eq('id', editingItem.id);
        if (error) throw error;
      } else {
        const { error } = await supabase.from('budget_items').insert({
          user_id: session.user.id,
          type: showForm,
          name: form.name,
          amount: parseFloat(form.amount) || 0,
          category: form.category,
        });
        if (error) throw error;
      }
      cancelForm();
      fetchItems();
    } catch (error) {
      console.error('Error saving budget item:', error);
      alert('Error saving budget item: ' + error.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteItem = async (id) => {
    if (!window.confirm('Delete this budget item?')) return;
    try {
      const { error } = await supabase.from('budget_items').delete().eq('id', id);
      if (error) throw error;
      fetchItems();
    } catch (error) {
      console.error('Error deleting budget item:', error);
      alert('Error deleting budget item: ' + error.message);
    }
  };

  // Liability payments brought in as virtual (read-only) expense rows
  const liabilityPayments = liabilities
    .filter((l) => l.monthly_payment != null && parseFloat(l.monthly_payment) > 0)
    .map((l) => ({
      id: `liability-${l.id}`,
      name: l.name,
      amount: parseFloat(l.monthly_payment),
      category: 'debt',
      fromLiability: true,
    }));

  const incomeItems = items.filter((i) => i.type === 'income');
  const expenseItems = items.filter((i) => i.type === 'expense');
  const shownExpenses = includeLiabilityPayments
    ? [...expenseItems, ...liabilityPayments]
    : expenseItems;

  const totalIncome = incomeItems.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const totalExpenses = shownExpenses.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);
  const leftover = totalIncome - totalExpenses;
  const liabilityPaymentTotal = liabilityPayments.reduce((s, i) => s + i.amount, 0);

  if (loading) {
    return <div className="text-center py-12 text-gray-500">Loading your budget...</div>;
  }

  const categoryLabel = (type, value) => {
    const list = type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES;
    return list.find((c) => c.value === value)?.label || value;
  };

  return (
    <div className="space-y-8">
      {/* Summary */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <div className="card p-6 flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Monthly Income</p>
            <p className="text-2xl font-bold text-success-600 mt-1">{formatCurrency(totalIncome)}</p>
          </div>
          <div className="h-10 w-10 rounded-lg bg-success-50 flex items-center justify-center shrink-0">
            <ArrowUpCircle className="h-5 w-5 text-success-600" />
          </div>
        </div>
        <div className="card p-6 flex items-start justify-between">
          <div>
            <p className="text-sm font-medium text-gray-600">Monthly Expenses</p>
            <p className="text-2xl font-bold text-danger-600 mt-1">{formatCurrency(totalExpenses)}</p>
          </div>
          <div className="h-10 w-10 rounded-lg bg-danger-50 flex items-center justify-center shrink-0">
            <ArrowDownCircle className="h-5 w-5 text-danger-600" />
          </div>
        </div>
        <div className={`rounded-lg border shadow-sm p-6 flex items-start justify-between ${leftover >= 0 ? 'bg-primary-50 border-primary-200' : 'bg-danger-50 border-danger-200'}`}>
          <div>
            <p className="text-sm font-medium text-gray-600">{leftover >= 0 ? 'Left Over' : 'Over Budget'}</p>
            <p className={`text-2xl font-bold mt-1 ${leftover >= 0 ? 'text-primary-600' : 'text-danger-600'}`}>
              {formatCurrency(leftover)}
            </p>
          </div>
          <div className={`h-10 w-10 rounded-lg flex items-center justify-center shrink-0 ${leftover >= 0 ? 'bg-primary-100' : 'bg-danger-100'}`}>
            <Wallet className={`h-5 w-5 ${leftover >= 0 ? 'text-primary-600' : 'text-danger-600'}`} />
          </div>
        </div>
      </div>

      {/* Auto-import liability payments toggle */}
      <div className="card p-4 flex items-start sm:items-center gap-3">
        <input
          id="includeLiabilityPayments"
          type="checkbox"
          checked={includeLiabilityPayments}
          onChange={(e) => setIncludeLiabilityPayments(e.target.checked)}
          className="mt-1 sm:mt-0 h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
        />
        <label htmlFor="includeLiabilityPayments" className="text-sm text-gray-700 cursor-pointer">
          <span className="font-medium">Auto-import liability payments</span> from the Net Worth tab as expenses
          {liabilityPayments.length > 0 ? (
            <span className="text-gray-500"> — {liabilityPayments.length} payment{liabilityPayments.length !== 1 ? 's' : ''}, {formatCurrency(liabilityPaymentTotal)}/mo</span>
          ) : (
            <span className="text-gray-400"> — none yet (add a monthly payment to a liability)</span>
          )}
        </label>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* Income */}
        <Section
          title="Income"
          accent="green"
          items={incomeItems}
          onAdd={() => openForm('income')}
          onEdit={editItem}
          onDelete={deleteItem}
          categoryLabel={(v) => categoryLabel('income', v)}
          emptyText="No income added yet"
        />

        {/* Expenses */}
        <Section
          title="Expenses"
          accent="red"
          items={shownExpenses}
          onAdd={() => openForm('expense')}
          onEdit={editItem}
          onDelete={deleteItem}
          categoryLabel={(v) => categoryLabel('expense', v)}
          emptyText="No expenses added yet"
        />
      </div>

      {/* Add / Edit Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white p-6 rounded-lg shadow-xl max-w-md w-full">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">
              {editingItem ? 'Edit' : 'Add'} {showForm === 'income' ? 'Income' : 'Expense'}
            </h3>
            <form onSubmit={saveItem} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder={showForm === 'income' ? 'e.g., Paycheck' : 'e.g., Rent'}
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Monthly Amount ($)</label>
                <input
                  type="number"
                  step="0.01"
                  value={form.amount}
                  onChange={(e) => setForm({ ...form, amount: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="0.00"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => setForm({ ...form, category: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  {(showForm === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((c) => (
                    <option key={c.value} value={c.value}>{c.label}</option>
                  ))}
                </select>
              </div>
              <div className="flex gap-3 pt-2">
                <button
                  type="button"
                  onClick={cancelForm}
                  className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 text-sm font-medium"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={saving}
                  className="flex-1 px-4 py-2 bg-blue-600 hover:bg-blue-700 disabled:bg-blue-300 text-white rounded-lg text-sm font-medium"
                >
                  {saving ? 'Saving...' : editingItem ? 'Save Changes' : 'Add'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

const Section = ({ title, accent, items, onAdd, onEdit, onDelete, categoryLabel, emptyText }) => {
  const accentBtn = accent === 'green' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700';
  const accentText = accent === 'green' ? 'text-green-600' : 'text-red-600';
  const total = items.reduce((s, i) => s + (parseFloat(i.amount) || 0), 0);

  return (
    <div className="card">
      <div className="p-6 border-b border-gray-200 flex justify-between items-center">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          <p className={`text-sm font-semibold ${accentText}`}>{formatCurrency(total)}/mo</p>
        </div>
        <button onClick={onAdd} className={`btn btn-sm text-white ${accentBtn}`}>
          <Plus className="h-4 w-4 mr-1.5" /> Add {title === 'Income' ? 'Income' : 'Expense'}
        </button>
      </div>
      <div className="p-6">
        {items.length === 0 ? (
          <p className="text-gray-500 text-center py-8">{emptyText}</p>
        ) : (
          <div className="divide-y divide-gray-200">
            {items.map((item) => (
              <div key={item.id} className="flex items-center justify-between py-3 group">
                <div>
                  <p className="font-medium text-gray-900">
                    {item.name}
                    {item.fromLiability && (
                      <span className="ml-2 text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">
                        from liability
                      </span>
                    )}
                  </p>
                  <p className="text-sm text-gray-500">{categoryLabel(item.category)}</p>
                </div>
                <div className="flex items-center space-x-3">
                  <span className={`font-semibold ${accentText}`}>{formatCurrency(item.amount)}</span>
                  {!item.fromLiability && (
                    <>
                      <button
                        onClick={() => onEdit(item)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-blue-500 text-sm transition-opacity"
                      >
                        ✏️
                      </button>
                      <button
                        onClick={() => onDelete(item.id)}
                        className="opacity-0 group-hover:opacity-100 text-gray-400 hover:text-red-500 text-xl transition-opacity"
                      >
                        ×
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Budget;
